<script setup lang="ts">
import { ConfirmationPopup, DialogModal } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { reactive, ref, toRaw, watchEffect } from 'vue'

import { useUpdateParticipantCostsRequest } from '@/api'
import RefreshIcon from '@/assets/icons/refresh.svg?component'
import TrafficSources from '@/components/CampaignForm/TrafficSources.vue'
import { notification } from '@/lib/helpers'
import type { TrafficSourceCostsType } from '@/lib/types/traffic-source-costs.types'
import { BoxComponent, ButtonComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  campaignId: number
  participantId: string
  currency: string
  campaignCosts?: TrafficSourceCostsType[]
  individualCosts?: TrafficSourceCostsType[]
}>()
const isOpen = defineModel<boolean>('modelValue', { default: false })
const emits = defineEmits(['onSuccess'])

const formData = reactive<{ trafficSourceCosts: TrafficSourceCostsType[] }>({ trafficSourceCosts: [] })
const ruleFormRef = ref<FormInstance>()
const rules = reactive<FormRules>({})
const isResetPopupOpen = ref(false)
const { overrideCosts, isLoading, error } = useUpdateParticipantCostsRequest()

const handleSave = async (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate(async (valid) => {
    if (valid) {
      await overrideCosts(props.campaignId, props.participantId, formData.trafficSourceCosts, props.currency)
      isOpen.value = false
      if (!error.value) {
        notification.success('Commission was updated successfully')
      }
      emits('onSuccess')
    }
  })
}

const handleReset = async () => {
  isResetPopupOpen.value = false
  await overrideCosts(props.campaignId, props.participantId, null, props.currency)
  isOpen.value = false
  if (!error.value) {
    notification.success('Commission limit was reset successfully')
  }
  emits('onSuccess')
}

watchEffect(() => {
  if (props.individualCosts?.length) {
    formData.trafficSourceCosts = structuredClone(toRaw(props.individualCosts))
    return
  } else if (props.campaignCosts?.length) {
    formData.trafficSourceCosts = structuredClone(toRaw(props.campaignCosts))
  }
})
</script>
<template>
  <DialogModal v-model="isOpen" :align-center="true" @before-close="isOpen = false">
    <template #header>
      <TypographyComponent variant="headline-medium">Edit commission</TypographyComponent>
    </template>
    <template #default>
      <BoxComponent :mt="24" :mb="24" class="costs-overrider-modal__container">
        <el-form
          ref="ruleFormRef"
          :model="formData"
          :rules="rules"
          hide-required-asterisk
          :validate-on-rule-change="false"
          :scroll-to-error="true"
          label-position="top"
        >
          <BoxComponent :mb="24">
            <TrafficSources
              v-model:traffic-source-costs="formData.trafficSourceCosts"
              :currency="currency"
              :is-changing-sources-disabled="true"
              :group-by-source="false"
            />
          </BoxComponent>
        </el-form>
        <ConfirmationPopup
          v-model="isResetPopupOpen"
          :is-loading="isLoading"
          :align-center="true"
          width="400"
          title="Reset costs"
          description="Are you sure you want to reset costs to the campaign's values?"
          submit-btn-variant="primary"
          submit-btn-text="Confirm"
          @on-submit="handleReset"
        />
      </BoxComponent>
    </template>
    <template #footer>
      <FlexComponent justify="space-between" :gap="8">
        <ButtonComponent v-if="individualCosts?.length" variant="text" @click="isResetPopupOpen = true">
          <template #iconLeft>
            <RefreshIcon />
          </template>
          Reset to defaults
        </ButtonComponent>
        <BoxComponent ml="auto">
          <FlexComponent :gap="8">
            <ButtonComponent variant="text" @click="isOpen = false">Cancel</ButtonComponent>
            <ButtonComponent :is-loading="isLoading" @click="handleSave(ruleFormRef)">Save</ButtonComponent>
          </FlexComponent>
        </BoxComponent>
      </FlexComponent>
    </template>
  </DialogModal>
</template>
<style lang="scss" scoped>
.reset-button {
  align-content: flex-end;
  &:hover {
    background: var(--button-hover);
  }
}
.costs-overrider-modal__container {
  max-height: calc(70vh - 144px);
  overflow: auto;
}
.slide-enter-active,
.slide-leave-active {
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 100px;
  opacity: 1;
}
</style>
